import React, { useEffect } from 'react';
import { Text } from '@doda/io-react/common';
import { TextWithLF } from '@doda/common/src/components/TextWithLF';
import { toSalaryExamplesString } from '@doda/common/src/logics/jobSearchDetail/salary';
import { VisibleAnalytics } from '@doda/common/src/components/Analytics/VisibleAnalytics';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { JobDetailProps, TabProps } from '.';
import { SectionItem } from '../SectionItem';
import styles from './index.module.scss';
import { createJobProperty } from './logics/createJobProperty';
import { TeamMembers } from './TeamMembers';
import {
  measureVisibleCompanyPageLinkBanner,
  measureVisibleSalary,
} from '../../../logics/analytics';
import { PromoteApplicationModalDisplayTarget } from '../../PromoteApplicationModal';
import { CompanyPageLinkBanner } from './CompanyPageLinkBanner';
import { DrImages } from './DrImages';
import { FreeTextArea } from './FreeTextArea';
import { JobGlossaryLink } from '../JobGlossaryLink';
import { SectionTitle } from '../SectionTitle';
import { JobOfferKindAgent } from './JobOfferKindAgent';
import { CompanyOverview } from './CompanyOverview';

/**
 * T原稿のProps
 */
interface ManuscriptTProps extends JobDetailProps, TabProps {}

/**
 * T原稿
 */
export const ManuscriptT: React.FC<ManuscriptTProps> = ({
  job,
  isScout,
  initialTab,
  wcnsId,
  isApplied,
}) => {
  const dispatch = useAppDispatch();
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const {
    jid,
    jobState,
    access,
    workTime,
    workTimeReason,
    formDivision,
    period,
    jobContentOutline,
    jobContentDetail,
    targetMemberOutline,
    targetMemberDetail,
    freeSpaces,
    members,
    salary,
    salaryExamples,
    raise,
    bonus,
    treatmentAndWelfare,
    holiday,
    hasCompanyPageLink,
    companyId,
    corporateName,
    businessOutline,
    marketName,
    drImageList,
    selectionPoint,
    selectionInformation,
    recruitBackGround,
    ageLimitDisplay,
    indicatorReason,
    isRead,
    hasArticleContent,
  } = createJobProperty(job);
  const isVisibleCompanyPageLinkBanner = Boolean(
    hasCompanyPageLink && corporateName && companyId && jid
  );

  useEffect(() => {
    if (isAdobeScript) {
      measureVisibleCompanyPageLinkBanner(isVisibleCompanyPageLinkBanner, jid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript]);

  return (
    <div>
      <SectionTitle mb="L" border sideItem={<JobGlossaryLink />}>
        募集要項
      </SectionTitle>
      {recruitBackGround && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="事業内容・募集背景">
            <TextWithLF text={recruitBackGround} />
          </SectionItem>
        </div>
      )}
      {(jobContentDetail || jobContentOutline) && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="仕事内容">
            <TextWithLF text={jobContentOutline || jobContentDetail} />
            {jobContentOutline && jobContentDetail && (
              <>
                <Hr />
                <TextWithLF text={jobContentDetail} />
              </>
            )}
          </SectionItem>
        </div>
      )}
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <SectionItem label="対象となる方">
          <TextWithLF text={targetMemberOutline} />
          {(targetMemberDetail || ageLimitDisplay || indicatorReason) && (
            <>
              <Hr />
              {targetMemberDetail && <TextWithLF text={targetMemberDetail} />}
              {ageLimitDisplay && <TextWithLF text={ageLimitDisplay} />}
              {indicatorReason && <TextWithLF text={indicatorReason} />}
            </>
          )}
        </SectionItem>
      </div>
      {selectionPoint && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="選考のポイント">
            <TextWithLF text={selectionPoint} />
          </SectionItem>
        </div>
      )}
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <SectionItem label="勤務地">
          {jobState && <TextWithLF text={jobState} />}
          {access && (
            <>
              <Hr />
              <TextWithLF text={access} />
            </>
          )}
        </SectionItem>
      </div>
      {(workTime || workTimeReason) && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="勤務時間">
            {workTime && <TextWithLF text={workTime} />}
            {workTimeReason && <TextWithLF text={workTimeReason} />}
          </SectionItem>
        </div>
      )}
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <SectionItem label="雇用形態">
          {formDivision && <Text>{formDivision}</Text>}
          {period && (
            <>
              <Hr />
              <TextWithLF text={period} />
            </>
          )}
        </SectionItem>
      </div>
      {salary && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <PromoteApplicationModalDisplayTarget {...{ isScout, isRead }}>
            <VisibleAnalytics
              onVisible={() => measureVisibleSalary(isAdobeScript, dispatch)}
            >
              <SectionItem label="給与">
                <TextWithLF text={salary} />
              </SectionItem>
            </VisibleAnalytics>
          </PromoteApplicationModalDisplayTarget>
        </div>
      )}
      {salaryExamples.length !== 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="給与例">
            <TextWithLF text={toSalaryExamplesString(salaryExamples, 5)} />
          </SectionItem>
        </div>
      )}
      {raise && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="昇給">
            <TextWithLF text={raise} />
          </SectionItem>
        </div>
      )}
      {bonus && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="賞与">
            <TextWithLF text={bonus} />
          </SectionItem>
        </div>
      )}
      {treatmentAndWelfare && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="待遇・福利厚生">
            <TextWithLF text={treatmentAndWelfare} />
          </SectionItem>
        </div>
      )}
      {holiday && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionItem label="休日・休暇">
            <TextWithLF text={holiday} />
          </SectionItem>
        </div>
      )}
      {freeSpaces?.length > 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <FreeTextArea freeSpaces={freeSpaces} />
        </div>
      )}
      {members.length !== 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <TeamMembers members={members} />
        </div>
      )}
      {drImageList.length !== 0 && (
        <div className={styles['jobSearchDetail-body__sectionDr']}>
          <DrImages drImageList={drImageList} jid={jid} />
        </div>
      )}
      {businessOutline && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <CompanyOverview
            businessOutline={businessOutline}
            marketName={marketName}
            isManuscriptT
            jid={jid}
            initialTab={initialTab}
            wcnsId={wcnsId}
            isApplied={isApplied}
          />
        </div>
      )}
      {isVisibleCompanyPageLinkBanner && (
        <div className={styles['jobSearchDetail-body__companyPageLink']}>
          <CompanyPageLinkBanner
            companyId={companyId}
            corporateName={corporateName}
            jid={jid}
            hasArticleContent={hasArticleContent}
          />
        </div>
      )}
      {selectionInformation?.applicationMethod ? (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SectionTitle mb="L" border>
            応募方法
          </SectionTitle>
          <div className={styles['jobSearchDetail-body__applicationMethod']}>
            <SectionItem label="応募方法">
              <TextWithLF text={selectionInformation.applicationMethod} />
              ※求人情報は、掲載期間が過ぎますとサイト上から削除されます。
              <br />
              ※応募した求人内容を確認したい場合は、本ページを印刷されるなどのご対応をお願いいたします。
            </SectionItem>
            {selectionInformation?.selectionProcess && (
              <SectionItem label="選考プロセス">
                <TextWithLF text={selectionInformation.selectionProcess} />
              </SectionItem>
            )}
            {selectionInformation?.conntact && (
              <SectionItem label="連絡先">
                <TextWithLF text={selectionInformation.conntact} />
              </SectionItem>
            )}
          </div>
        </div>
      ) : (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <JobOfferKindAgent />
        </div>
      )}
    </div>
  );
};

const Hr = () => <hr className={styles['jobSearchDetail-body__hr']} />;
